import { applicationRemoteFeedback, applicationFeedbackText, isApplicationRemoteFeedback, type ApplicationRemoteFeedback } from "./applicationFeedbackPresentation";
import { mediaLibraryRemoteFeedback, mediaLibraryRemoteText, type MediaLibraryRemoteFeedback } from "./mediaLibraryFeedbackPresentation";
import { formatBytesParts as storageParts } from "../components/system/system-dashboard/utils";
import { getI18n, useTranslation } from "react-i18next";
import source from "./locales/en/mediaLibrary";
import { streamNumber, streamLocale, streamFixed } from "./streamingDashboardPresentation";
export { streamLocale, streamNumber, streamBytes, streamDate, streamUnit, streamRelative, streamFixed, streamAgo } from "./streamingDashboardPresentation";

export type MediaLibraryTextKey = keyof typeof source;
export type MediaLibraryLocalMessage = { key: MediaLibraryTextKey; values?: Record<string, string | number> };
export type MediaLibraryMessage = MediaLibraryLocalMessage | MediaLibraryRemoteFeedback | ApplicationRemoteFeedback;
/** Only source-owned identities enter translation; unknown detail stays literal. */
export function mlText(key: MediaLibraryTextKey, values?: Record<string, string | number>): string {
  const engine = getI18n();
  const plural = values && typeof values.count === "number" ? `${key}_${new Intl.PluralRules(streamLocale()).select(values.count)}` : key;
  if (Object.prototype.hasOwnProperty.call(source, plural)) key = plural as MediaLibraryTextKey;
  const formatted = values && Object.fromEntries(Object.entries(values).map(([name, value]) => [name, typeof value === "number" ? streamNumber(value) : value]));
  if (!engine?.isInitialized) return source[key].replace(/{{\s*(\w+)\s*}}/g, (_match, name: string) => String(formatted?.[name] ?? ""));
  return (engine as unknown as { t: (key: MediaLibraryTextKey, options: Record<string, unknown>) => string }).t(key, { ...formatted, ns: "mediaLibrary", keySeparator: false });
}
export function useMediaLibraryLanguage(): void { useTranslation("mediaLibrary"); }
export const mlMessage = (message: string | MediaLibraryMessage | null): string | null => typeof message === "object" && message !== null ? "remote" in message ? isApplicationRemoteFeedback(message) ? applicationFeedbackText(message) : mediaLibraryRemoteText(message as MediaLibraryRemoteFeedback) : mlText(message.key, message.values) : message;
export class MediaLibraryLocalError extends Error { constructor(readonly presentation: MediaLibraryMessage) { super("remote" in presentation ? "media-feedback" : presentation.key); } }
export const mlFeedback = (error: unknown, fallback: MediaLibraryTextKey): string | MediaLibraryMessage => error instanceof MediaLibraryLocalError ? error.presentation : error instanceof Error ? error.message : { key: fallback };

export const mlLocal = (key: MediaLibraryTextKey, values?: Record<string, string | number>): MediaLibraryLocalMessage => ({ key, values });
export const mlException = (message: string | MediaLibraryMessage): Error => typeof message === "string" ? new Error(message) : new MediaLibraryLocalError(message);

export const mlAgo = (value: number, unit: Intl.RelativeTimeFormatUnit): string => new Intl.RelativeTimeFormat(streamLocale(), { numeric: "auto", style: "short" }).format(-value, unit);
export const mlUnit = (value: number, unit: string): string => new Intl.NumberFormat(streamLocale(), { style: "unit", unit, unitDisplay: "short", maximumFractionDigits: 1 }).format(value);

/** Interpolate React slots as nodes; translated text never becomes HTML. */
export function mlNodes(key: MediaLibraryTextKey, values: Record<string, import("react").ReactNode>): import("react").ReactNode[] {
  const count = values.count;
  if (typeof count === "number") {
    const variant = `${key}_${new Intl.PluralRules(streamLocale()).select(count)}`;
    if (Object.prototype.hasOwnProperty.call(source, variant)) key = variant as MediaLibraryTextKey;
  }
  const nodes = Object.values(values).map(value => typeof value === "number" ? streamNumber(value) : value);
  const markers = Object.fromEntries(Object.keys(values).map((name, index) => [name, `\uFFF0${index}\uFFF1`]));
  return mlText(key, markers).split(/(\uFFF0\d+\uFFF1)/g).map(part => {
    const match = /^\uFFF0(\d+)\uFFF1$/.exec(part);
    return match ? nodes[Number(match[1])] : part;
  });
}


/** Keep public message strings compatible while retaining local identity for mounted feedback. */
export function mlOutcome(value: string | MediaLibraryMessage): { message: string; presentation?: MediaLibraryMessage } {
  return typeof value === "string" ? { message: value } : { get message() { return mlMessage(value)!; }, presentation: value };
}

/** Retain the shared storage thresholds and precision while localizing its numeric display. */
export function mlStorageParts(bytes?: number | null): { value: string; unit: string } {
  const parts = storageParts(bytes);
  return { ...parts, value: parts.value === "—" ? parts.value : streamFixed(Number(parts.value), parts.value.split(".")[1]?.length ?? 0) };
}
export function mlStorageBytes(bytes?: number | null): string {
  const parts = mlStorageParts(bytes);
  return parts.unit ? `${parts.value} ${parts.unit}` : parts.value;
}

/** Preserve server identity until the current viewer renders it. */
export const mlServer = (response: unknown, fallback: string | MediaLibraryMessage): string | MediaLibraryMessage => {
  const retained = applicationRemoteFeedback(response) ?? mediaLibraryRemoteFeedback(response);
  if (retained) return retained;
  if (response && typeof response === "object") {
    const value = response as { message?: unknown; error?: unknown };
    const nested = value.error && typeof value.error === "object" ? value.error as { message?: unknown } : undefined;
    if (typeof nested?.message === "string") return nested.message;
    if (typeof value.message === "string") return value.message;
    if (typeof value.error === "string") return value.error;
  }
  return fallback;
};
